export interface InvoiceData {
  financialYear: string;
  date: string;
  day: string;
  invoiceNo: string;
  customerName: string;
  quantity: number;
  gstNo: string;
  gstRate: number;
  taxable: number;
  igst: number;
  cgst: number;
  sgst: number;
  total: number;
  paid: number;
  balance: number;
  splitMode: string;
}

export interface InvoiceWiseDisplayRow extends InvoiceData {
  siNo: number;
  invoiceRowSpan: number;
  isFirstLineOfInvoice: boolean;
  /** Invoice-level grand total (paid + balance). */
  billValue: number;
}

export interface InvoiceWiseGrandTotals {
  taxable: number;
  igst: number;
  cgst: number;
  sgst: number;
  lineTotal: number;
  billValue: number;
  paid: number;
  balance: number;
}

const num = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Group line items by invoice; invoice-level fields appear once per invoice. */
export function buildInvoiceWiseDisplayRows(
  data: InvoiceData[],
): InvoiceWiseDisplayRow[] {
  const groups = new Map<string, InvoiceData[]>();
  data.forEach((row, index) => {
    const key = row.invoiceNo?.trim() || `__invoice_${index}`;
    const list = groups.get(key) ?? [];
    list.push(row);
    groups.set(key, list);
  });

  const result: InvoiceWiseDisplayRow[] = [];
  let siNo = 0;

  groups.forEach((lines: InvoiceData[]) => {
    siNo += 1;
    const first = lines[0];
    const paid = num(first.paid);
    const balance = num(first.balance);
    const billValue = paid + balance;
    const span = lines.length;

    lines.forEach((line: InvoiceData, idx: number) => {
      result.push({
        ...line,
        siNo,
        invoiceRowSpan: span,
        isFirstLineOfInvoice: idx === 0,
        billValue,
      });
    });
  });

  return result;
}

/** Sum line items once; count paid/balance once per invoice. */
export function computeInvoiceWiseGrandTotals(
  data: InvoiceData[],
): InvoiceWiseGrandTotals {
  const displayRows = buildInvoiceWiseDisplayRows(data);

  const lineSums = displayRows.reduce(
    (acc, row) => {
      acc.taxable += num(row.taxable);
      acc.igst += num(row.igst);
      acc.cgst += num(row.cgst);
      acc.sgst += num(row.sgst);
      acc.lineTotal += num(row.total);
      return acc;
    },
    { taxable: 0, igst: 0, cgst: 0, sgst: 0, lineTotal: 0 },
  );

  const seen = new Set<string>();
  let billValue = 0;
  let paid = 0;
  let balance = 0;

  for (const row of data) {
    const key = row.invoiceNo?.trim() || "";
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const p = num(row.paid);
    const b = num(row.balance);
    paid += p;
    balance += b;
    billValue += p + b;
  }

  return {
    ...lineSums,
    billValue,
    paid,
    balance,
  };
}
